// EVENTS LOG — the arithmetic behind /logs/events, with no React and no network
// so it runs under plain node (scripts/test-eventsLog.mjs).
//
// The page reads EVERY event in the chosen window and derives its tiles, tab
// counts and table from those same rows, so a tile can never disagree with the
// table under it. The windows are the Activity page's own (rangeFor in
// activityMetrics.js): America/Chicago calendar days, the same presets and the
// same ?range= / ?from= / ?to= link shape, so a window means one thing on both
// pages.
import { RANGE_KEYS, whenMs, chicagoDayOf, longDay } from './activityMetrics.js'

// The log opens on everything, newest first, which is what it showed before it
// had a range at all. Narrowing is the reader's move; a log that opened on
// today would hide yesterday's sign-ins behind a control nobody had touched.
export const EVENTS_DEFAULT_RANGE = 'all'

// ?range= as this page reads it: the menu's own keys only. The Activity page's
// retired 30d / 90d / ytd were never offered here, so no saved link carries
// them, and an unknown key opens the default rather than a window nobody chose.
export function eventsRangeKey(param) {
  return RANGE_KEYS.includes(param) ? param : EVENTS_DEFAULT_RANGE
}

// Rows whose time falls inside [since, until], both ends inclusive; a missing
// end is open. The bounds are the instants rangeFor hands the events API, so
// the browser's own mirror is clipped to exactly the window the server answers
// for. A row with no readable time belongs to no window, and an unreadable
// bound matches nothing rather than quietly opening the window.
export function eventsInWindow(rows, since, until) {
  const lo = since == null || since === '' ? -Infinity : whenMs(since)
  const hi = until == null || until === '' ? Infinity : whenMs(until)
  if (lo == null || hi == null) return []
  return (rows || []).filter((e) => {
    const t = e ? whenMs(e.createdAt) : null
    return t != null && t >= lo && t <= hi
  })
}

// The Chicago day of the oldest readable row, or '' when there is none. A
// custom range picked from All time (which has no first day of its own) starts
// here, so switching to a custom range does not collapse the table to today.
export function oldestDay(rows) {
  let oldest = ''
  for (const e of rows || []) {
    const day = e ? chicagoDayOf(e.createdAt) : ''
    if (day && (!oldest || day < oldest)) oldest = day
  }
  return oldest
}

// The tiles and the tab counts, from the rows the table shows.
//   total          every row
//   emailsSent     rows of type email_sent
//   activeMembers  distinct actors, case-folded: the earliest rows were stamped
//                  with whatever the client sent, so 'Owner@Example.com' and
//                  'owner@example.com' are one person. A row with no actor is
//                  the system, not a member.
//   byGroup        { group: count }, grouped by groupOf(type)
export function eventStats(rows, groupOf = () => '') {
  let total = 0
  let emailsSent = 0
  const actors = new Set()
  const byGroup = {}
  for (const e of rows || []) {
    if (!e) continue
    total += 1
    if (e.type === 'email_sent') emailsSent += 1
    const actor = String(e.actor || '').trim().toLowerCase()
    if (actor) actors.add(actor)
    const group = groupOf(e.type)
    if (group) byGroup[group] = (byGroup[group] || 0) + 1
  }
  return { total, emailsSent, activeMembers: actors.size, byGroup }
}

const RANGE_PHRASES = {
  today: 'today',
  yesterday: 'yesterday',
  week: 'this week',
  lastweek: 'last week',
  month: 'this month',
  '3m': 'in the past 3 months',
}

// What an empty table says, named by its window, so an empty stretch is never
// mistaken for an empty log. All time (no first day) keeps the empty-log copy.
export function emptyEventsMessage(range) {
  if (!range || !range.sinceDay) {
    return 'No events yet — activity like sign-ins, enrichment, drafts, and sent emails shows up here as it happens.'
  }
  const phrase = RANGE_PHRASES[range.key]
  if (phrase) return `No events ${phrase}.`
  if (range.sinceDay === range.untilDay) return `No events on ${longDay(range.sinceDay)}.`
  return `No events between ${longDay(range.sinceDay)} and ${longDay(range.untilDay)}.`
}

// Plain words for where the rows came from whenever that is not simply "the
// whole workspace log for this window"; null when it is.
//   read        { mode, status, complete, events } as the page stored it
//   configured  whether this build signs requests at all. Without Cognito
//               (plain local dev) the mirror IS the record.
// tone 'info' is a quiet line. 'warn' is the amber band: the rows are short or
// are only this browser's, and the tiles print '—' under it.
export function sourceNotice(read, configured) {
  if (!read) return null
  const count = (read.events || []).length.toLocaleString()
  if (read.mode === 'api') {
    if (read.complete) return null
    const ranOut = read.status >= 200 && read.status < 300
    return {
      tone: 'warn',
      text: ranOut
        ? `Only the newest ${count} events in this range could be read in time, so the table is incomplete and the counts are unavailable. Pick a shorter range to see the rest.`
        : `Only the newest ${count} events in this range were read before the events service stopped answering${read.status ? ` (HTTP ${read.status})` : ''}, so the table is incomplete and the counts are unavailable. Reload the page or pick a shorter range.`,
    }
  }
  if (!configured) return { tone: 'info', text: 'Local mode: these are the events recorded in this browser.' }
  const why =
    read.status === 401
      ? 'your session has expired, so sign in again'
      : read.status === 403
        ? 'this account is not on the workspace roster'
        : read.status
          ? `the events service answered HTTP ${read.status}`
          : 'the events service could not be reached'
  return {
    tone: 'warn',
    text: `The workspace log could not be read: ${why}. The table shows only the events recorded in this browser, and the counts are unavailable.`,
  }
}
